import { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SparklineChart } from '@/components/SparklineChart';

import { API_BASE_URL } from '../config/api';

const BACKEND_URL = API_BASE_URL;

const LOGIN_URL = 'https://my.tdfxcapital.net/portal/login';

const instrumentsConfig = {
  popular: [
    { symbol: 'EURUSD', name: 'EUR/USD', type: 'forex', apiSymbol: 'EURUSD' },
    { symbol: 'BTC', name: 'Bitcoin', type: 'crypto', binanceSymbol: 'BTCUSDT' },
    { symbol: 'GOLD', name: 'Gold', type: 'commodity', apiSymbol: 'GOLD' },
    { symbol: 'SPX', name: 'S&P 500', type: 'index', apiSymbol: 'SPX' },
    { symbol: 'ETH', name: 'Ethereum', type: 'crypto', binanceSymbol: 'ETHUSDT' },
  ],
  forex: [
    { symbol: 'EURUSD', name: 'EUR/USD', type: 'forex', apiSymbol: 'EURUSD' },
    { symbol: 'GBPUSD', name: 'GBP/USD', type: 'forex', apiSymbol: 'GBPUSD' },
    { symbol: 'USDJPY', name: 'USD/JPY', type: 'forex', apiSymbol: 'USDJPY' },
    { symbol: 'AUDUSD', name: 'AUD/USD', type: 'forex', apiSymbol: 'AUDUSD' },
    { symbol: 'USDCHF', name: 'USD/CHF', type: 'forex', apiSymbol: 'USDCHF' },
  ],
  crypto: [
    { symbol: 'BTC', name: 'Bitcoin', type: 'crypto', binanceSymbol: 'BTCUSDT' },
    { symbol: 'ETH', name: 'Ethereum', type: 'crypto', binanceSymbol: 'ETHUSDT' },
    { symbol: 'XRP', name: 'Ripple', type: 'crypto', binanceSymbol: 'XRPUSDT' },
    { symbol: 'ADA', name: 'Cardano', type: 'crypto', binanceSymbol: 'ADAUSDT' },
    { symbol: 'SOL', name: 'Solana', type: 'crypto', binanceSymbol: 'SOLUSDT' },
  ],
  indices: [
    { symbol: 'DJI', name: 'Dow Jones', type: 'index', apiSymbol: 'DJI' },
    { symbol: 'IXIC', name: 'NASDAQ', type: 'index', apiSymbol: 'IXIC' },
    { symbol: 'SPX', name: 'S&P 500', type: 'index', apiSymbol: 'SPX' },
    { symbol: 'FTSE', name: 'FTSE 100', type: 'index', apiSymbol: 'FTSE' },
    { symbol: 'GDAXI', name: 'DAX', type: 'index', apiSymbol: 'GDAXI' },
  ],
};

const formatPrice = (price, type) => {
  if (type === 'forex') return price.toFixed(5);
  if (type === 'crypto') {
    if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (price >= 1) return price.toFixed(2);
    return price.toFixed(4);
  }
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const InstrumentsSection = () => {
  const [activeTab, setActiveTab] = useState('popular');
  const [instruments, setInstruments] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showAllInstruments, setShowAllInstruments] = useState(false);

  const fetchWithTimeout = async (url, timeout = 15000) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(timer);
      return await res.json();
    } catch {
      clearTimeout(timer);
      return null;
    }
  };

  const fetchAllPrices = useCallback(async () => {
    try {
      const [cryptoRes, forexRes, indicesRes, commoditiesRes] = await Promise.all([
        fetchWithTimeout(`${BACKEND_URL}/api/crypto-prices`),
        fetchWithTimeout(`${BACKEND_URL}/api/forex-prices`),
        fetchWithTimeout(`${BACKEND_URL}/api/indices-prices`),
        fetchWithTimeout(`${BACKEND_URL}/api/commodities-prices`),
      ]);

      const crypto = cryptoRes?.success ? cryptoRes.prices : null;
      const forex = forexRes?.success ? forexRes.prices : null;
      const indices = indicesRes?.success ? indicesRes.prices : null;
      const commodities = commoditiesRes?.success ? commoditiesRes.prices : null;

      const updatedInstruments = {};

      for (const [category, items] of Object.entries(instrumentsConfig)) {
        updatedInstruments[category] = items.map((item) => {
          if (item.type === 'crypto' && crypto && crypto[item.binanceSymbol]) {
            const data = crypto[item.binanceSymbol];
            return {
              ...item,
              price: formatPrice(data.price, 'crypto'),
              change: data.change >= 0 ? `+${data.change.toFixed(2)}` : data.change.toFixed(2),
              changePercent: data.changePercent >= 0 ? `+${data.changePercent.toFixed(2)}%` : `${data.changePercent.toFixed(2)}%`,
              isPositive: data.changePercent >= 0,
            };
          }

          if (item.type === 'forex' && forex && forex[item.apiSymbol]) {
            const data = forex[item.apiSymbol];
            return {
              ...item,
              price: formatPrice(data.price, 'forex'),
              change: '+0.00000',
              changePercent: '+0.00%',
              isPositive: true,
            };
          }

          if (item.type === 'index' && indices && indices[item.apiSymbol]) {
            const data = indices[item.apiSymbol];
            return {
              ...item,
              price: formatPrice(data.price, 'index'),
              change: data.change >= 0 ? `+${data.change.toFixed(2)}` : data.change.toFixed(2),
              changePercent: data.changePercent >= 0 ? `+${data.changePercent.toFixed(2)}%` : `${data.changePercent.toFixed(2)}%`,
              isPositive: data.changePercent >= 0,
            };
          }

          if (item.type === 'commodity' && commodities && commodities[item.apiSymbol]) {
            const data = commodities[item.apiSymbol];
            return {
              ...item,
              price: formatPrice(data.price, 'commodity'),
              change: data.change >= 0 ? `+${data.change.toFixed(2)}` : data.change.toFixed(2),
              changePercent: data.changePercent >= 0 ? `+${data.changePercent.toFixed(2)}%` : `${data.changePercent.toFixed(2)}%`,
              isPositive: data.changePercent >= 0,
            };
          }

          return {
            ...item,
            price: 'Loading...',
            change: '---',
            changePercent: '---',
            isPositive: true,
          };
        });
      }

      setInstruments(updatedInstruments);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Error updating prices:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllPrices();
    const interval = setInterval(fetchAllPrices, 10000);
    return () => clearInterval(interval);
  }, [fetchAllPrices]);

  const handleViewAll = () => {
    setShowAllInstruments(true);
  };

  const handleTradeClick = () => {
    window.open(LOGIN_URL, '_blank');
  };

  return (
    <section id="instruments" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Trade Global Markets
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-2">
            Access hundreds of instruments with competitive spreads and lightning-fast execution
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>
              {loading ? 'Loading live prices...' : 
                lastUpdate ? `Live prices • Updated ${lastUpdate.toLocaleTimeString()}` : 
                'Connecting to live feed...'}
            </span>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-4 mb-8">
            <TabsTrigger value="popular" className="text-sm font-semibold">Popular</TabsTrigger>
            <TabsTrigger value="forex" className="text-sm font-semibold">Forex</TabsTrigger>
            <TabsTrigger value="crypto" className="text-sm font-semibold">Crypto</TabsTrigger>
            <TabsTrigger value="indices" className="text-sm font-semibold">Indices</TabsTrigger>
          </TabsList>

          {Object.keys(instrumentsConfig).map((category) => (
            <TabsContent key={category} value={category}>
              <div className="max-w-5xl mx-auto">
                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 rounded-t-lg border-b border-gray-200">
                  <div className="col-span-3 text-sm font-semibold text-gray-700">Instrument</div>
                  <div className="col-span-2 text-sm font-semibold text-gray-700 text-right">Price</div>
                  <div className="col-span-2 text-sm font-semibold text-gray-700 text-right">Change</div>
                  <div className="col-span-3 text-sm font-semibold text-gray-700 text-center">Chart</div>
                  <div className="col-span-2 text-sm font-semibold text-gray-700 text-right">Action</div>
                </div>

                <div className="bg-white border border-gray-200 rounded-b-lg overflow-hidden">
                  {!instruments[category] || instruments[category].length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <span className="ml-3 text-gray-600">Fetching live prices...</span>
                    </div>
                  ) : (
                    instruments[category]
                      .slice(0, showAllInstruments ? undefined : 5)
                      .map((instrument, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-2 md:grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                        data-testid={`instrument-row-${instrument.symbol || index}`}
                      >
                        <div className="col-span-2 md:col-span-3 flex items-center">
                          <span className="font-semibold text-gray-900">{instrument.name}</span>
                        </div>
                        <div className="col-span-1 md:col-span-2 text-right flex items-center justify-end">
                          <span className="font-mono text-lg font-semibold text-gray-900">
                            {instrument.price}
                          </span>
                        </div>
                        <div className="col-span-1 md:col-span-2 text-right flex items-center justify-end gap-2">
                          {instrument.change !== '---' && instrument.price !== 'Loading...' && (
                            instrument.isPositive ? (
                              <TrendingUp className="h-4 w-4 text-green-600" />
                            ) : (
                              <TrendingDown className="h-4 w-4 text-red-600" />
                            )
                          )}
                          <div className="flex flex-col items-end">
                            <span className={`font-mono text-sm font-semibold ${
                              instrument.change === '---' || instrument.price === 'Loading...' ? 'text-gray-400' :
                              instrument.isPositive ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {instrument.change}
                            </span>
                            <span className={`font-mono text-xs ${
                              instrument.changePercent === '---' || instrument.price === 'Loading...' ? 'text-gray-400' :
                              instrument.isPositive ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {instrument.changePercent}
                            </span>
                          </div>
                        </div>
                        <div className="hidden md:flex col-span-3 items-center justify-center">
                          {instrument.price !== 'Loading...' && (
                            <SparklineChart
                              symbol={instrument.binanceSymbol || instrument.apiSymbol || instrument.symbol?.replace('/', '')}
                              isPositive={instrument.isPositive}
                            />
                          )}
                        </div>
                        <div className="col-span-2 md:col-span-2 flex justify-end">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs font-semibold"
                            disabled={instrument.price === 'Loading...'}
                            onClick={handleTradeClick}
                          >
                            Trade
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="text-center mt-8">
                  {!showAllInstruments && instruments[category]?.length > 5 ? (
                    <Button 
                      variant="outline" 
                      size="lg" 
                      className="font-semibold"
                      onClick={handleViewAll}
                    >
                      View All {category.charAt(0).toUpperCase() + category.slice(1)} Instruments
                    </Button>
                  ) : showAllInstruments ? (
                    <Button 
                      variant="outline" 
                      size="lg" 
                      className="font-semibold"
                      onClick={() => setShowAllInstruments(false)}
                    >
                      Show Less
                    </Button>
                  ) : null}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

export default InstrumentsSection;